import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { AutoBookingShell, BookingDetails } from "./AutoCancelPage";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AutoReschedulePage() {
  const { token } = useParams();
  const { t, locale } = useI18n();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [doneDate, setDoneDate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/hosts/auto/${token}`).then((r) => setBooking(unwrap(r)))
      .catch((e) => setError(e?.response?.status === 404 ? "invalid" : "error"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!newDate) { toast.error(t("hosts.resched_new")); return; }
    setBusy(true);
    try {
      const r = await api.post(`/hosts/auto/${token}/reschedule`, { preferred_date: newDate });
      setDoneDate(unwrap(r)?.preferred_date || newDate);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not reschedule");
    } finally { setBusy(false); }
  };

  return (
    <AutoBookingShell>
      <SEO title="Reschedule cleaning — Ever Clean Home" noindex />
      <Card className="rounded-3xl p-8 border border-[#E5E7EB] max-w-md w-full" data-testid="auto-reschedule-page">
        {loading ? (
          <div className="grid place-items-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>
        ) : error ? (
          <div className="text-center py-6" data-testid="auto-reschedule-error">
            <XCircle className="w-10 h-10 mx-auto text-[#9CA3AF]" />
            <p className="mt-3 text-[#4B5563]">{t("hosts.manage_invalid")}</p>
          </div>
        ) : doneDate ? (
          <div className="text-center py-4" data-testid="auto-reschedule-done">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#0EA5A4]" />
            <h1 className="font-display text-2xl font-extrabold tracking-tighter mt-4">{t("hosts.resched_done")}</h1>
            <p className="mt-2 text-[#4B5563]">{t("hosts.resched_done_body", { date: formatDate(doneDate, locale) })}</p>
            <Link to="/"><Button variant="outline" className="rounded-xl mt-6">{t("hosts.back_home")}</Button></Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-extrabold tracking-tighter">{t("hosts.resched_title")}</h1>
            <p className="mt-2 text-sm text-[#4B5563]">{t("hosts.resched_sub")}</p>
            <div className="mt-5"><BookingDetails booking={booking} t={t} locale={locale} /></div>
            <div className="mt-5">
              <Label className="text-sm">{t("hosts.resched_new")}</Label>
              <Input type="date" min={todayPlus(0)} value={newDate} onChange={(e) => setNewDate(e.target.value)} className="rounded-xl mt-1" data-testid="auto-reschedule-date" />
            </div>
            <Button onClick={submit} disabled={busy || !newDate} className="w-full rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white mt-5" data-testid="auto-reschedule-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("hosts.resched_submit")}
            </Button>
          </>
        )}
      </Card>
    </AutoBookingShell>
  );
}
